import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { RxIntuitiveTeethOrchestratorComponent } from '@modules/rx-intuitive-teeth-orchestrator/containers/rx-intuitive-teeth-orchestrator/rx-intuitive-teeth-orchestrator.component';
import { RxIntuitiveTreatmentTableContainerComponent } from '@modules/rx-intuitive-teeth-orchestrator/containers/rx-intuitive-treatment-table-container/rx-intuitive-treatment-table-container.component';
import { RxIntuitiveTeethHelperService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-teeth-helper.service';
import { RxIntuitiveToothEditorRoter } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-tooth-editor-roter.service';
import { RxIntuitiveTeethOrchestratorQuery } from '@modules/rx-intuitive-teeth-orchestrator/state/rx-intuitive-teeth-orchestrator-query';
import {
	OrchestratorViewMode,
	RxIntuitiveTeethOrchestratorStore
} from '@modules/rx-intuitive-teeth-orchestrator/state/rx-intuitive-teeth-orchestrator-store';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { RxIntuitiveImplantsViewModalService } from '../../components/rx-intuitive-implants-view-modal/rx-intuitive-implants-view-modal.service';
import { RxIntuitiveTeethOrchestratorFacade } from '../../rx-intuitive-teeth-orchestrator.facade';
import { RxIntuitiveToothEditorFacade } from '../../rx-intuitive-tooth-editor.facade';
import { INDICATION_OPERATIONS } from '../../services/intuitive-teeth-diagram-button-strategy/indication-context.token';
import { DefaultStrategy } from '../../services/intuitive-teeth-diagram-button-strategy/intuitive-indication.strategy';
import { IntuitiveIndicationService } from '../../services/intuitive-teeth-diagram-button-strategy/rx-intuitive-indication-opearations/intuitive-indication-operations.service';
import { RxIntuitiveCopyFromBehaviorService } from '../../services/rx-intuitive-copy-from-behavior.service';
import { RxIntuitiveIndicationMapperService } from '../../services/rx-intuitive-indication-mapper.service';
import { RxIntuitiveIndicationRulesService } from '../../services/rx-intuitive-indication-rules.service';
import { RxIntuitiveSpriteService } from '../../services/rx-intuitive-sprite-service';
import { RxIntuitiveToothTreatmentMapper } from '../../services/rx-intuitive-tooth-treatment-mapper.service';
import { RxIntuitiveTeethDiagramMutationService } from '../../services/teeth-diagram/rx-intuitive-teeth-diagram-mutation.service';
import { RxIntuitiveTeethDiagramViewService } from '../../services/teeth-diagram/rx-intuitive-teeth-diagram-view.service';
import { RxIntuitiveToothEditorMutationService } from '../../services/tooth-editor/rx-intuitive-tooth-editor-mutation.service';
import { RxIntuitiveToothEditorReadOnlyService } from '../../services/tooth-editor/rx-intuitive-tooth-editor-read-only.service';
import { RxIntuitiveToothEditorViewService } from '../../services/tooth-editor/rx-intuitive-tooth-editor-view.service';
import { combineLatest, map, Observable, takeUntil } from 'rxjs';
import { RxIntuitiveArtOrchestratorComponent } from '@modules/rx-intuitive-art/containers/rx-intuitive-art-orchestrator/rx-intuitive-art-orchestrator.component';
import { RxIntuitiveTeethBridgeOrchestratorComponent } from '../rx-intuitive-teeth-bridge-orchestrator/rx-intuitive-teeth-bridge-orchestrator.component';

export type OrchestratorMode = 'base' | 'bridge' | 'art';

@Component({
	selector: 'rx-intuitive-teeth-orchestrator-container',
	templateUrl: './rx-intuitive-teeth-orchestrator-container.component.html',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		RxIntuitiveTeethOrchestratorQuery,
		RxIntuitiveTeethOrchestratorStore,
		RxIntuitiveImplantsViewModalService,
		RxIntuitiveTeethHelperService,
		RxIntuitiveToothEditorRoter,
		RxIntuitiveIndicationMapperService,
		RxIntuitiveTeethOrchestratorFacade,
		RxIntuitiveToothEditorFacade,
		RxIntuitiveIndicationRulesService,
		RxIntuitiveToothTreatmentMapper,
		RxIntuitiveCopyFromBehaviorService,
		RxIntuitiveSpriteService,
		// Composition services required by RxIntuitiveToothEditorFacade
		RxIntuitiveToothEditorReadOnlyService,
		RxIntuitiveToothEditorMutationService,
		RxIntuitiveToothEditorViewService,
		// Composition services required by RxIntuitiveTeethDiagramFacade
		RxIntuitiveTeethDiagramMutationService,
		RxIntuitiveTeethDiagramViewService,
		// Indication strategy wiring for the default (non-bridge) context
		IntuitiveIndicationService,
		{ provide: INDICATION_OPERATIONS, useExisting: IntuitiveIndicationService },
		DefaultStrategy
	],
	imports: [
		CommonModule,
		RxIntuitiveTeethOrchestratorComponent,
		RxIntuitiveTeethBridgeOrchestratorComponent,
		RxIntuitiveTreatmentTableContainerComponent,
		RxIntuitiveArtOrchestratorComponent
	]
})
export class RxIntuitiveTeethOrchestratorContainerComponent extends BaseDestroyableComponent implements OnInit {
	readonly isToothChartViewMode$ = this.facade.orchestratorViewMode$.pipe(
		map(viewMode => viewMode === OrchestratorViewMode.chart)
	);

	readonly isTreatmentTableViewMode$ = this.facade.orchestratorViewMode$.pipe(map(viewMode => viewMode === OrchestratorViewMode.table));

	readonly mode$: Observable<OrchestratorMode> = combineLatest([this.facade.isArtProcedureSelected$, this.facade.hasSelectedBridge$]).pipe(
		map(([isArtProcedureSelected, hasSelectedBridge]) => {
			if (isArtProcedureSelected) {
				return 'art';
			}

			return hasSelectedBridge ? 'bridge' : 'base';
		})
	);

	constructor(private readonly facade: RxIntuitiveTeethOrchestratorFacade) {
		super();
	}

	ngOnInit(): void {
		this.facade.fetchRxAndSyncStore$.pipe(takeUntil(this.componentAlive$)).subscribe();
	}
}
